import { useState } from "react";
import { login, type AuthUser } from "../api";
import { useCurrentTheme } from "../lib/theme";
import { Logo } from "./Logo";
import { OceanField } from "./webgl/OceanField";

interface Props {
  onSuccess: (user: AuthUser) => void;
}

export function Login({ onSuccess }: Props) {
  const theme = useCurrentTheme();
  const [username, setUsername] = useState("njrc");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lockupScrim =
    theme === "light"
      ? [
          "radial-gradient(ellipse 74% 58% at 50% 14%, rgba(242, 233, 214, 0.34), transparent 66%)",
          "radial-gradient(ellipse 92% 82% at 50% 58%, rgba(13, 4, 8, 0.78), transparent 74%)",
        ].join(", ")
      : [
          "radial-gradient(ellipse 74% 58% at 50% 14%, rgba(214, 190, 122, 0.20), transparent 66%)",
          "radial-gradient(ellipse 92% 82% at 50% 58%, rgba(5, 2, 4, 0.76), transparent 74%)",
        ].join(", ");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login(username, password);
      onSuccess(user);
    } catch (err) {
      window.dispatchEvent(new CustomEvent("damon:bark", { detail: { id: "login-fail" } }));
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dark-tile relative min-h-screen overflow-hidden bg-[color:var(--color-bg)]">
      <OceanField />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6">
        <div
          className="login-lockup fade-in flex w-full max-w-[520px] flex-col items-center px-5 py-8 sm:px-10 sm:py-10"
          style={{
            background: lockupScrim,
            borderRadius: 8,
            boxShadow: "0 24px 90px rgba(0, 0, 0, 0.34)",
          }}
        >
          <div className="flex w-full max-w-sm flex-col items-center">
            {/* Brand - the logo's wordmark is the wordmark; no need for redundant text. */}
            <div className="mb-10 flex w-full flex-col items-center text-center">
              <div
                style={{
                  filter:
                    "drop-shadow(-14px 0 44px rgba(228, 0, 43, 0.55)) drop-shadow(14px 0 44px rgba(0, 70, 255, 0.55)) drop-shadow(0 6px 16px rgba(0,0,0,0.7))",
                  width: "min(480px, 90vw)",
                  maxWidth: "100%",
                }}
              >
                <Logo size={480} />
              </div>
              <p className="login-tagline meta mt-6 text-center text-[color:var(--color-text-dim)]">
                NJ municipal court records, served with judgment
              </p>
            </div>

            <form onSubmit={onSubmit} className="login-form-card w-full space-y-3">
              <div>
                <label htmlFor="login-user" className="label mb-1.5 block">
                  Username
                </label>
                <input
                  id="login-user"
                  autoComplete="username"
                  className="input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div>
                <label htmlFor="login-pw" className="label mb-1.5 block">
                  Password
                </label>
                <input
                  id="login-pw"
                  type="password"
                  autoComplete="current-password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {error && (
                <div className="rounded-lg border border-[color:var(--color-rose-hi)] bg-[color:var(--color-rose-soft)] px-3 py-2 text-sm text-[color:var(--color-rose-hi)]">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !password}
                className="btn btn-primary login-enter-button w-full"
                style={{ paddingBlock: 12 }}
              >
                {submitting ? "Signing in..." : "Enter"}
              </button>
            </form>

            <p className="login-helper mt-6 text-center text-[11px] text-[color:var(--color-text-dim)]">
              Your session is remembered on this device for 30 days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
